import { createAction } from '@reduxjs/toolkit';
import { notAvailable } from 'core/common/constants';
import { normalizeError } from 'core/common/errors';
import { ATTRS_ANALYTICS_FUNNEL_NAME, SpanName } from 'core/common/observability/entities';
import { AppThunk } from 'core/common/store';
import { config } from 'core/config';
import { getFunnelName } from 'core/funnel/store/getFunnelName';
import { getEmail, getOrderData } from 'core/user/store';
import { getUserLocation } from 'core/user/store/selectors';
import { PaymentError, RecurringData } from '../../entities';
import { PaymentUpsellError } from '../../errors';
import { updateOrderId } from './paymentForm';
import { initializeResignFormRequest, resignFormFailed } from './resignForm';

export const recurringPaymentThreeDomainSecure = createAction<RecurringData>(
  'payments/RECURRING_PAYMENT_THREE_DOMAIN_SECURE',
);

export const recurringPaymentPending = createAction('payments/RECURRING_PAYMENT/pending');

export const recurringPaymentSucceed = createAction('payments/RECURRING_PAYMENT/succeed');

export const recurringPaymentFailed = createAction<PaymentError>(
  'payments/RECURRING_PAYMENT/failed',
);

export const recurringPaymentRequest =
  (offerId: string): AppThunk =>
  async (dispatch, getState, context) => {
    const {
      purchaseService,
      analyticsService,
      loggingService: logger,
      observabilitySystem,
      errorMonitoringService,
    } = context;

    const orderData = getOrderData(getState());
    const userLocation = getUserLocation(getState());
    const funnelName = getFunnelName(getState());
    const email = getEmail(getState());

    dispatch(recurringPaymentPending());

    const spanAttributes = {
      [ATTRS_ANALYTICS_FUNNEL_NAME]: funnelName,
    };

    const span = observabilitySystem.startSpan(SpanName.RECURRING_PAYMENT_REQUEST);
    span.setAttributes(spanAttributes);
    span.addEvent('Pending');

    try {
      if (config.oneTimePaymentUpsells.includes(offerId)) {
        const activePayments = await purchaseService.getActivePayments(offerId, email);

        if (!!activePayments.length) {
          throw new PaymentUpsellError();
        }
      }

      const result = await purchaseService.purchaseReport(offerId, orderData);

      if (result.status === '3ds_verify') {
        dispatch(recurringPaymentThreeDomainSecure(result));
        return;
      }

      if (result.status === 'fail_auth' || result.status === 'fail') {
        if (!result.tokenId) {
          dispatch(createOrderByOfferRequest(offerId));
          return;
        }

        dispatch(initializeResignFormRequest(offerId, result.tokenId));
        return;
      }

      analyticsService.purchaseSucceed(offerId);
      dispatch(recurringPaymentSucceed());

      span.addEvent('Succeed');
      span.succeed();
    } catch (err) {
      analyticsService.purchaseError(offerId);
      const error = normalizeError(err);

      span.addEvent('Failed');
      span.fail(error);

      logger.error(error);

      dispatch(createOrderByOfferRequest(offerId));
      dispatch(recurringPaymentFailed(error));
      analyticsService.trackEvent('recurring_payment_failed', {
        message: error.message,
        offerId,
      });

      errorMonitoringService.captureError(error, {
        tags: {
          offer_id: offerId,
          get_params: location.search || notAvailable,
          error_message: error.message,
          error_reason: 'recurring_payment_failed',
          funnel_name: funnelName,
          ...userLocation,
        },
      });
    }
  };

export const createOrderByOfferRequest =
  (offerId: string): AppThunk =>
  async (dispatch, _, context) => {
    const { purchaseService } = context;

    const data = await purchaseService.createOrderByOfferId(offerId);

    dispatch(updateOrderId(data.orderId));
    dispatch(resignFormFailed());
  };
